import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../../core/components/Button';
import { useImmerAtom } from 'jotai-immer';
import {
    invoiceLineItemPageReconcileAtom,
    invoiceLineItemPageReconcileIdAtom,
    invoiceLineItemPageReconcileIdsAtom,
} from './cp_order_invoices_details_atoms';

type UseReconcileModalStateReturn = {
    id: string | undefined;
    setId: (id: string | undefined) => void;
    notes: string;
    setNotes: (val: string) => void;
    modalOpen: boolean;
    setModalOpen: (val: boolean) => void;
    reconciledIds: string[];
    setReconciledIds: (val: string) => void;
};

export const useReconcileModalState = (): UseReconcileModalStateReturn => {
    const [reconciledItems, _setReconciledItems] = useImmerAtom(invoiceLineItemPageReconcileIdsAtom);
    const [modalOpen, _setModalOpen] = useImmerAtom(invoiceLineItemPageReconcileAtom);
    const [id, _setId] = useImmerAtom(invoiceLineItemPageReconcileIdAtom);
    const [notes, setNotes] = useState<string>('');

    const setModalOpen = useCallback(
        (val: boolean): void => {
            _setModalOpen(() => val);
        },
        [_setModalOpen],
    );

    const setReconciledItems = useCallback(
        (val: string): void => {
            _setReconciledItems(draft => [...draft, val]);
        },
        [_setReconciledItems],
    );

    const setId = useCallback((id: string | undefined): void => _setId(() => id), [_setId]);

    useEffect(() => {
        return (): void => {
            _setReconciledItems(() => []);
        };
    }, [_setReconciledItems]);

    return {
        id,
        setId,
        notes,
        setNotes,
        modalOpen,
        setModalOpen,
        reconciledIds: reconciledItems,
        setReconciledIds: setReconciledItems,
    };
};

const ReconcileLinkRenderer = <TItem extends { [key: string]: any }>({
    item,
    /* eslint-disable-next-line */
    colKey,
}: {
    item: TItem;
    colKey: string;
}): JSX.Element => {
    const { setId, setModalOpen, reconciledIds } = useReconcileModalState();

    const isReconciled = reconciledIds.includes(item.LineId);

    const handleSetModal = useCallback((): void => {
        setId(item.LineId);
        setModalOpen(true);
    }, [setModalOpen, setId, item.LineId]);

    return <>{item.Overallocated && !isReconciled ? <Button onClick={handleSetModal}>Reconcile</Button> : null}</>;
};

export default ReconcileLinkRenderer;
